import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { BehaviorSubject, Subject, takeUntil } from "rxjs";
import {
  DxButtonModule,
  DxContextMenuModule,
  DxTextBoxModule,
  DxValidatorComponent,
  DxValidatorModule,
} from "devextreme-angular";
import { CommonModule } from "@angular/common";
import { ProjectHubBoardListItem } from "./project-hub.types";
import {
  ActivatedRoute,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
} from "@angular/router";
import { ProjectHubService } from "src/app/services/project-hub.service";
import { AppGlobals } from "src/app/app.globals";
import {
  GenericPopupComponent,
  PopupButton,
} from "src/app/components/generic-popup/generic-popup.component";
import { GlobalPopupService } from "src/app/components/generic-popup/presets/global-popup/global-popup.service";
import { ContextMenuItemClickEvent } from "devextreme/ui/file_manager";
import { SvgIconComponent } from "src/app/components/svg-icon/svg-icon.component";
import { SparkHistoryOverlayComponent } from "src/app/pages/grid-connection/project/project-tabs/project-hub/components/spark-history-overlay/spark-history-overlay.component";
import { GpdButtonComponent } from "src/app/components/gpd-button/gpd-button.component";
import { ActivityLogService } from "src/app/services/activity-log.service";
import { ActivityEntity, ActivitySection } from "src/app/constants/activity-log";

@Component({
    selector: "app-project-hub",
    imports: [
        CommonModule,
        DxButtonModule,
        RouterLink,
        RouterLinkActive,
        RouterOutlet,
        DxTextBoxModule,
        GenericPopupComponent,
        DxValidatorModule,
        DxContextMenuModule,
        SvgIconComponent,
        SparkHistoryOverlayComponent,
        GpdButtonComponent
    ],
    templateUrl: "./project-hub.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: "./project-hub.component.css"
})
export class ProjectHubComponent implements OnInit, OnDestroy {

  BoardEditButtons: PopupButton[] = [
    {
      text: $localize`:@@cancel:Cancel`,
      onClick: () => {
        this.editingBoard = null;
        this.createBoardPopupVisible = false;
      },
      type: "secondary",
    },
    {
      text: $localize`:@@save:Save`,
      onClick: () => {
        this.onSaveBoardClick().then((result) => {
          
          if (result) {
            this.editingBoard = null;
            this.createBoardPopupVisible = false;
          } 
        });
      },
      submitBehavior: true,
    },
  ];

  private readonly $unsubscribe = new Subject<void>();

  // State
  projectId: number;
  boardId: number | null = null;
  editingBoard: ProjectHubBoardListItem | null = null;
  createBoardPopupVisible = false;
  deleteBoardPopupVisible = false;
  loadingBoards = false;
  boardsLoaded = false;

  boardList: ProjectHubBoardListItem[] = [];
  newSparksMap: { [boardId: number]: number } = {};

  @ViewChild("boardNameValidator", { static: false })
  boardNameValidator: DxValidatorComponent;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    public projectHubService: ProjectHubService,
    private readonly popupService: GlobalPopupService,
    private cdr: ChangeDetectorRef,
    private activityLog: ActivityLogService
  ) {}

  ngOnInit(): void {
    this.init();
  }

  async init() {
    this.projectId = parseInt(this.route.snapshot.paramMap.get("id"));
    this.boardId = parseInt(
      this.route.snapshot.firstChild?.paramMap.get("boardId")
    );
    if (this.boardId == null || this.boardId == undefined) {
      this.boardId = parseInt(localStorage.getItem("projectHubBoardId"));
      this.router.navigate([this.boardId], {
        relativeTo: this.route,
      });
    }

    await this.loadBoards();
    this.projectHubService.sparkDeletedSubject
      .pipe(takeUntil(this.$unsubscribe))
      .subscribe(async () => {
        await this.loadBoards();
      });

    this.projectHubService.newSparksMap$
      .pipe(takeUntil(this.$unsubscribe))
      .subscribe((map) => {
        this.newSparksMap = map;
      });

    this.route.paramMap
      .pipe(takeUntil(this.$unsubscribe))
      .subscribe(params => {
        const newProjectId = parseInt(params.get("id"));
        if (newProjectId && newProjectId !== this.projectId) {
          this.projectId = newProjectId;
          this.loadBoards();
        }
      });
  }

  ngOnDestroy(): void {
    this.$unsubscribe.next();
    this.$unsubscribe.complete();
  }

  // Log the board the user actually lands on, once the board list has resolved which that is.
  private lastLoggedBoardId: number | null = null;

  private logBoardView(): void {
    const board = this.boardList?.find((b) => b.id === this.boardId);
    if (!board || this.lastLoggedBoardId === board.id) return;
    this.lastLoggedBoardId = board.id;
    this.activityLog.logView({
      Section: ActivitySection.PROJECT_HUB,
      EntityType: ActivityEntity.SPARK_BOARD,
      EntityId: board.id,
      EntityLabel: board.name,
      ProjectId: this.projectId,
    });
  }

  async loadBoards() {
    this.loadingBoards = true;
    try {
      const result = await this.projectHubService.GetBoardList(this.projectId);
      if (result.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
        return;
      }
      this.boardList = result.boards;
      this.logBoardView();
      // Fetch new sparks for each board
      const newSparksMap: { [boardId: number]: number } = {};
      result.boards.forEach((board) => {
        newSparksMap[board.id] = board.unreadSparksCount;
      });

      this.projectHubService.setNewSparksMap(newSparksMap);
      if (
        (!this.boardId && this.boardList.length > 0) ||
        (this.boardId &&
          !this.boardList.find((b) => b.id === this.boardId) &&
          this.boardList.length > 0)
      ) {
        this.router.navigate([this.boardList[0]?.id], {
          relativeTo: this.route,
        });
      }
    } 
    catch (error) {
      AppGlobals.ShowErrorMessage(
        $localize`:@@internalServerError:Internal Server Error`
      );
    } 
    finally {
      this.loadingBoards = false;
      this.boardsLoaded = true;
      this.cdr.detectChanges();
    }
  }

  async writeBoard(board: ProjectHubBoardListItem) {

    try {
      if (this.editingBoard == null && board.sparkCount > 0 && board.isDeleted) {
        board.isDeleted = false;
        AppGlobals.ShowErrorMessage(
          $localize`:@@boardHasSparks:Board has sparks`,
        );
        return;
      }
      const result = await this.projectHubService.WriteBoard({
        board: board,
        projectId: this.projectId,
      });
      if (result.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
        return;
      }
      this.boardId = result.resultDataInt;
      this.router.navigate([this.boardId], {
        relativeTo: this.route,
      });
      this.editingBoard = null;
      this.loadBoards();
    } catch (error) {
      AppGlobals.ShowErrorMessage($localize`:@@internalServerError:Internal Server Error`,);
    }
    return true;
  }

  onCreateBoardClick() {
    this.editingBoard = {
      id: null,
      name: null,
      isDeleted: false,
    };
    this.createBoardPopupVisible = true;
  }

  async onSaveBoardClick(): Promise<boolean> {
    const t = this.boardNameValidator.instance.validate();
    if (!t.isValid) {
      return false;
    }
    return await this.writeBoard(this.editingBoard);
  }

  onContextMenuItemClick(event: ContextMenuItemClickEvent, board: any) {
    if (event.itemIndex === 0) {
      this.editingBoard = { ...board };
      this.createBoardPopupVisible = true;
    } else if (event.itemIndex === 1) {
      this.onDeleteBoardClick(board);
    }
  }

  onDeleteBoardClick(board: any) {
    this.popupService.showDeletePopup({
      title: $localize`:@@deleteBoardTitle:Delete Board`,
      content: $localize`:@@deleteBoardContent:Are you sure you want to delete this board?`,
      onConfirm: () => {
        board.isDeleted = true;
        this.writeBoard(board);
      },
    });
  }

  boardNameExists(value: string) {
    return this.boardList?.find((b) => b.name === value) == null;
  }
}
